import { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
const API = "https://affinityconcept.tech/api";

const STATUS_BADGE = {
  hot: "bg-red-100 text-red-600",
  warm: "bg-orange-100 text-orange-600",
  cold: "bg-blue-100 text-blue-600",
  new: "bg-green-100 text-green-600",
};

const TABS = [
  { key: "messages", label: "💬 Messages" },
  { key: "leads", label: "👥 Leads" },
  { key: "projects", label: "🏗️ Projects" },
  { key: "reminders", label: "🔔 Reminders" },
];

export default function Agent({ token: tokenProp, onLogout }) {
  const [tab, setTab] = useState("messages");
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", status: "warm", interest: "", budget: "", notes: "" });
  const [newProject, setNewProject] = useState({ name: "", location: "", price_range: "", units: "", delivery: "", commission: "", status: "active", notes: "" });

  const token = tokenProp || localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => { fetchAll(); }, [tab]);
  useEffect(() => { axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setUser(r.data)).catch(() => {}); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [msgs, lds, prjs, rems] = await Promise.all([
        fetch(`${API}/whatsapp/messages`, { headers }).then(r => r.json()),
        fetch(`${API}/whatsapp/leads`, { headers }).then(r => r.json()),
        fetch(`${API}/whatsapp/projects`, { headers }).then(r => r.json()),
        fetch(`${API}/whatsapp/reminders`, { headers }).then(r => r.json()),
      ]);
      setMessages(Array.isArray(msgs) ? msgs : []);
      setLeads(Array.isArray(lds) ? lds : []);
      setProjects(Array.isArray(prjs) ? prjs : []);
      setReminders(Array.isArray(rems) ? rems : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function addLead() {
    await fetch(`${API}/whatsapp/leads`, { method: "POST", headers, body: JSON.stringify(newLead) });
    setShowAddLead(false);
    setNewLead({ name: "", phone: "", status: "warm", interest: "", budget: "", notes: "" });
    fetchAll();
  }

  async function addProject() {
    await fetch(`${API}/whatsapp/projects`, { method: "POST", headers, body: JSON.stringify(newProject) });
    setShowAddProject(false);
    setNewProject({ name: "", location: "", price_range: "", units: "", delivery: "", commission: "", status: "active", notes: "" });
    fetchAll();
  }

  async function doneReminder(id) {
    await fetch(`${API}/whatsapp/reminders/${id}/done`, { method: "PUT", headers });
    fetchAll();
  }

  const inputClass = "w-full mb-3 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent";
  const primaryBtn = "bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition";
  const badge = (cls) => `inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">🏠 Real Estate Agent</h1>
          <p className="text-gray-500 text-sm">WhatsApp lead management and AI analysis</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Total Leads</p>
            <p className="text-xl font-bold text-gray-900">{leads.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Hot Leads</p>
            <p className="text-xl font-bold text-red-500">{leads.filter(l => l.status === "hot").length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Active Projects</p>
            <p className="text-xl font-bold text-green-500">{projects.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Pending Reminders</p>
            <p className="text-xl font-bold text-orange-500">{reminders.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition -mb-px border-b-2 ${
                tab === t.key
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-center text-gray-400 py-10 text-sm">Loading...</div>}

        {/* Messages */}
        {!loading && tab === "messages" && (
          <div className="space-y-3">
            {messages.length === 0 && (
              <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100 text-center text-gray-400">
                <div className="text-3xl mb-2">💬</div>
                <div className="text-sm">No messages yet. Connect WhatsApp to start receiving messages.</div>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start mb-1.5">
                  <div className="text-sm font-semibold text-gray-900">{m.sender_name || m.sender_phone}</div>
                  <div className="text-xs text-gray-400">{new Date(m.received_at).toLocaleString("en-GB")}</div>
                </div>
                <div className={`text-sm text-gray-600 ${m.ai_analysis ? "mb-3" : ""}`}>{m.message}</div>
                {m.ai_analysis && (
                  <div className="bg-green-50 rounded-xl px-3 py-2.5 border-l-4 border-green-500">
                    <div className="text-xs text-green-600 font-semibold mb-1">✦ AI ANALYSIS</div>
                    <div className="text-xs text-gray-600 whitespace-pre-wrap">{m.ai_analysis}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Leads */}
        {!loading && tab === "leads" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button className={primaryBtn} onClick={() => setShowAddLead(true)}>+ Add Lead</button>
            </div>
            {leads.length === 0 && (
              <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100 text-center text-gray-400">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-sm">No leads yet.</div>
              </div>
            )}
            {leads.map(l => (
              <div key={l.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">{l.name}</div>
                    <div className="text-xs text-gray-500 mb-1.5">{l.phone}</div>
                    {l.interest && <div className="text-sm text-gray-600 mb-1">🏠 {l.interest}</div>}
                    {l.budget && <div className="text-sm text-gray-600">💰 {l.budget}</div>}
                  </div>
                  <span className={badge(STATUS_BADGE[l.status] || "bg-gray-100 text-gray-600")}>{l.status?.toUpperCase()}</span>
                </div>
                {l.notes && <div className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">{l.notes}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Projects */}
        {!loading && tab === "projects" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button className={primaryBtn} onClick={() => setShowAddProject(true)}>+ Add Project</button>
            </div>
            {projects.length === 0 && (
              <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100 text-center text-gray-400">
                <div className="text-3xl mb-2">🏗️</div>
                <div className="text-sm">No projects yet.</div>
              </div>
            )}
            {projects.map(p => (
              <div key={p.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm font-semibold text-gray-900">{p.name}</div>
                  <span className={badge(p.status === "active" ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600")}>{p.status?.toUpperCase()}</span>
                </div>
                {p.location && <div className="text-xs text-gray-500 mb-2">📍 {p.location}</div>}
                <div className="flex gap-2 flex-wrap">
                  {p.price_range && <span className={badge("bg-gray-100 text-gray-600")}>{p.price_range}</span>}
                  {p.delivery && <span className={badge("bg-green-100 text-green-600")}>📅 {p.delivery}</span>}
                  {p.commission && <span className={badge("bg-orange-100 text-orange-600")}>💵 {p.commission}</span>}
                  {p.units && <span className={badge("bg-blue-100 text-blue-600")}>{p.units} units</span>}
                </div>
                {p.notes && <div className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">{p.notes}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Reminders */}
        {!loading && tab === "reminders" && (
          <div className="space-y-3">
            {reminders.length === 0 && (
              <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100 text-center text-gray-400">
                <div className="text-3xl mb-2">✅</div>
                <div className="text-sm">All caught up!</div>
              </div>
            )}
            {reminders.map(r => (
              <div key={r.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">{r.action === "call" ? "📞" : "✉️"} {r.lead_name || "Lead"}</div>
                    <div className="text-sm text-gray-600 mb-1">{r.note}</div>
                    <div className={`text-xs ${r.priority === "high" ? "text-red-500" : r.priority === "medium" ? "text-orange-500" : "text-blue-500"}`}>
                      ⏰ {r.due_datetime ? new Date(r.due_datetime).toLocaleString("en-GB") : "—"}
                    </div>
                  </div>
                  <button onClick={() => doneReminder(r.id)} className="bg-green-100 hover:bg-green-200 text-green-600 px-3 py-1.5 rounded-lg text-xs font-medium transition">✓ Done</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Lead modal */}
        {showAddLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddLead(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Add New Lead</h2>
              <input className={inputClass} placeholder="Full Name *" value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} />
              <input className={inputClass} placeholder="Phone" value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} />
              <select className={inputClass} value={newLead.status} onChange={e => setNewLead({ ...newLead, status: e.target.value })}>
                <option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
              </select>
              <input className={inputClass} placeholder="Interest (e.g. 2BR Polanco)" value={newLead.interest} onChange={e => setNewLead({ ...newLead, interest: e.target.value })} />
              <input className={inputClass} placeholder="Budget (e.g. $400k USD)" value={newLead.budget} onChange={e => setNewLead({ ...newLead, budget: e.target.value })} />
              <textarea className={`${inputClass} h-20 resize-none`} placeholder="Notes" value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} />
              <div className="flex gap-2 justify-end">
                <button className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition" onClick={() => setShowAddLead(false)}>Cancel</button>
                <button className={primaryBtn} onClick={addLead}>Add Lead</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Project modal */}
        {showAddProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddProject(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Add New Project</h2>
              <input className={inputClass} placeholder="Project Name *" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
              <input className={inputClass} placeholder="Location" value={newProject.location} onChange={e => setNewProject({ ...newProject, location: e.target.value })} />
              <input className={inputClass} placeholder="Price Range (e.g. $300k-$600k USD)" value={newProject.price_range} onChange={e => setNewProject({ ...newProject, price_range: e.target.value })} />
              <input className={inputClass} placeholder="Units" type="number" value={newProject.units} onChange={e => setNewProject({ ...newProject, units: e.target.value })} />
              <input className={inputClass} placeholder="Delivery (e.g. Q3 2026)" value={newProject.delivery} onChange={e => setNewProject({ ...newProject, delivery: e.target.value })} />
              <input className={inputClass} placeholder="Commission (e.g. 3%)" value={newProject.commission} onChange={e => setNewProject({ ...newProject, commission: e.target.value })} />
              <textarea className={`${inputClass} h-20 resize-none`} placeholder="Notes" value={newProject.notes} onChange={e => setNewProject({ ...newProject, notes: e.target.value })} />
              <div className="flex gap-2 justify-end">
                <button className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition" onClick={() => setShowAddProject(false)}>Cancel</button>
                <button className={primaryBtn} onClick={addProject}>Add Project</button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
